import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import LoadingSpinner from '../LoadingSpinner';
import PageHeader from '../PageHeader';
import './ItemsGrid.css';

/**
 * A reusable grid component for displaying items with search and infinite scroll
 */
const ItemsGrid = ({
  title,
  subtitle,
  fetchItems,
  renderItem,
  placeholderText = 'Search items...',
  emptyStateMessage = 'No items found',
  itemsPerPage = 20,
  leftActions = null,
  rightActions = null,
  filterFunction = null,
}) => {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Ref for the observer to detect when user scrolls near bottom
  const observer = useRef();
  // Ref for the last item element in the list
  const lastItemRef = useRef();
  
  // Function to load initial items
  const loadInitialItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setPage(1);
      
      const response = await fetchItems(1, itemsPerPage);
      setAllItems(response.items || []);
      setHasMore(response.currentPage < response.totalPages);
    } catch (err) {
      setError('Failed to load items. Please try again.');
      console.error('Error loading items:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchItems, itemsPerPage]);
  
  // Function to load more items when scrolling
  const loadMoreItems = useCallback(async () => {
    if (loading || !hasMore) return;
    
    try {
      setLoading(true);
      const nextPage = page + 1;
      
      const response = await fetchItems(nextPage, itemsPerPage);
      setAllItems(prev => [...prev, ...(response.items || [])]);
      setPage(nextPage);
      setHasMore(response.currentPage < response.totalPages);
    } catch (err) {
      setError('Failed to load more items. Please try again.');
      console.error('Error loading more items:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, page, fetchItems, itemsPerPage]);
  
  // Handler for search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };
  
  // Calculate filtered items based on search term
  const filteredItems = searchTerm.trim() !== '' && filterFunction 
    ? allItems.filter(item => filterFunction(item, searchTerm))
    : allItems;
  
  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1,
    };
    
    const handleObserver = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading) {
        loadMoreItems();
      }
    };
    
    const currentObserver = new IntersectionObserver(handleObserver, options);
    observer.current = currentObserver;
    
    if (lastItemRef.current) {
      currentObserver.observe(lastItemRef.current);
    }
    
    return () => {
      if (currentObserver) {
        currentObserver.disconnect();
      }
    };
  }, [hasMore, loading, loadMoreItems]);
  
  // Load initial items on component mount
  useEffect(() => {
    loadInitialItems();
  }, [loadInitialItems]);
  
  return (
    <div className="items-grid-container">
      <PageHeader
        title={title}
        subtitle={subtitle}
        leftActions={leftActions}
        rightActions={rightActions}
      />
      
      <div className="search-container">
        <input 
          type="text" 
          className="search-input" 
          placeholder={placeholderText}
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
      
      <div className="items-container">
        {error ? (
          <div className="error-state">{error}</div>
        ) : loading ? (
          <div className="loading-state">
            <LoadingSpinner />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="empty-state">{emptyStateMessage}</div>
        ) : (
          <div className="items-grid">
            {filteredItems.map((item, index) => {
              // If this is the last item, attach the ref for intersection observer
              if (index === filteredItems.length - 1) {
                return (
                  <div 
                    key={item.id || index}
                    ref={lastItemRef}
                    className="grid-item-wrapper"
                  >
                    {renderItem(item, index)}
                  </div>
                );
              }
              
              return (
                <div 
                  key={item.id || index}
                  className="grid-item-wrapper"
                >
                  {renderItem(item, index)}
                </div>
              );
            })}
          </div>
        )}
        
        {loading && allItems.length > 0 && (
          <div className="loading-state">
            <LoadingSpinner />
          </div>
        )}
        
        {!loading && hasMore && allItems.length > 0 && (
          <div className="load-more-indicator">
            Scroll for more
          </div>
        )}
      </div>
    </div>
  );
};

ItemsGrid.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  fetchItems: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  placeholderText: PropTypes.string,
  emptyStateMessage: PropTypes.string,
  itemsPerPage: PropTypes.number,
  leftActions: PropTypes.node,
  rightActions: PropTypes.node,
  filterFunction: PropTypes.func
};

export default ItemsGrid;